import React from "react";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";

import { uploadLearningFile } from "./swipeDeckHelpers";
import { MediaSuggestionBanner } from "./SwipeCardMediaSuggestion";
import MediaLibraryModal from "./MediaLibraryModal";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import { PollResultsGroup, sumCounts } from "./pollResultsShared";

const QUESTION_MAX = 120;
// Generous safety-net cap, matching the AI-generation backstop in
// learning_swipe_deck.py. A tight 60-char limit silently truncated real
// sentence-length choices; 200 fits multi-line choices comfortably.
const CHOICE_MAX = 200;
const MIN_CHOICES = 2;
const MAX_CHOICES = 4;

const QUICK_EMOJIS = ["👍", "👎", "❤️", "🤔", "🔥", "⭐", "✅", "❌"];

export function SwipeCardPollEditor({ card, onChange, companyId, results }) {
  const content = card.content_json || {};
  const question = content.question || "";
  const choices =
    Array.isArray(content.choices) && content.choices.length >= MIN_CHOICES
      ? content.choices
      : [
          { text: "", emoji: null },
          { text: "", emoji: null },
        ];
  const showPercentages = content.show_percentages !== false; // default true
  const headerImageUrl = card.media_url || null;

  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState(null);
  const [libraryOpen, setLibraryOpen] = React.useState(false);

  // Read-only vote aggregates (manager view). Two independent audiences, kept
  // separate: anonymous public-link voters and assigned/enrolled employees.
  const publicCounts = results?.public || {};
  const assignedCounts = results?.assigned || {};
  const publicTotal = sumCounts(publicCounts);
  const assignedTotal = sumCounts(assignedCounts);
  const hasResults = publicTotal > 0 || assignedTotal > 0;

  // Votes are stored by choice position, so removing an option would re-index
  // (and thus misattribute) every later option's existing votes. Once a poll
  // has any responses, lock removal — renaming an option or adding a new one is
  // still safe (same/append index).
  const optionsLocked = hasResults;

  const updateContent = (patch) => {
    onChange({ ...card, content_json: { ...content, ...patch } });
  };

  const updateChoice = (index, patch) => {
    const next = choices.map((c, i) => (i === index ? { ...c, ...patch } : c));
    updateContent({ choices: next });
  };

  const addChoice = () => {
    if (choices.length >= MAX_CHOICES) return;
    updateContent({ choices: [...choices, { text: "", emoji: null }] });
  };

  const removeChoice = (index) => {
    // Blocked once the poll has votes (see optionsLocked) — removing would
    // re-index later options and misattribute their existing votes.
    if (optionsLocked || choices.length <= MIN_CHOICES) return;
    updateContent({ choices: choices.filter((_, i) => i !== index) });
  };

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setUploadError("Please choose an image file.");
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const { uuid, url } = await uploadLearningFile(companyId, file);
      onChange({ ...card, media_file_uuid: uuid, media_url: url });
    } catch (err) {
      setUploadError(err?.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleClearImage = () => {
    onChange({ ...card, media_file_uuid: null, media_url: null });
  };

  return (
    <div className="flex flex-col gap-5 p-5">
      {/* Question */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide">
            Question
          </label>
          <span
            className={[
              "text-[11px] tabular-nums",
              question.length > QUESTION_MAX
                ? "text-red-600 font-semibold"
                : "text-slate-400",
            ].join(" ")}
          >
            {question.length}/{QUESTION_MAX}
          </span>
        </div>
        <textarea
          value={question}
          onChange={(e) => updateContent({ question: e.target.value })}
          rows={2}
          maxLength={QUESTION_MAX}
          placeholder="Open-ended — there's no right answer"
          className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-100 text-sm resize-none"
        />
      </div>

      {/* Optional context image */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
          Context image (optional)
        </label>
        <MediaSuggestionBanner card={card} />
        {headerImageUrl ? (
          <div className="relative rounded-lg overflow-hidden border border-slate-200 bg-black">
            <img
              src={headerImageUrl}
              alt=""
              className="w-full h-28 object-cover"
            />
            <button
              type="button"
              onClick={handleClearImage}
              className="absolute top-2 right-2 px-2 py-1 rounded-md bg-black/70 text-white text-xs hover:bg-black/90"
            >
              Remove
            </button>
          </div>
        ) : (
          <label className="flex items-center justify-center h-20 rounded-lg border-2 border-dashed border-slate-300 hover:border-violet-500 cursor-pointer transition-colors text-xs text-slate-500">
            {uploading ? "Uploading…" : "Upload an image to set the scene"}
            <input
              type="file"
              accept="image/*"
              onChange={handleImagePick}
              disabled={uploading}
              className="hidden"
            />
          </label>
        )}
        <button
          type="button"
          onClick={() => setLibraryOpen(true)}
          disabled={uploading}
          className="mt-2 w-full px-3 py-1.5 text-xs font-medium text-violet-700 border border-violet-200 rounded-lg hover:bg-violet-50 transition-colors inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
        >
          <CollectionsOutlinedIcon style={{ fontSize: 14 }} />
          {headerImageUrl ? "Replace from library" : "Choose from library"}
        </button>
        {uploadError && (
          <div className="mt-1 text-xs text-red-600">{uploadError}</div>
        )}
        <MediaLibraryModal
          open={libraryOpen}
          onClose={() => setLibraryOpen(false)}
          companyId={companyId}
          onSelect={({ uuid, url }) =>
            onChange({ ...card, media_file_uuid: uuid, media_url: url })
          }
        />
      </div>

      {/* Choices */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide">
            Choices ({choices.length}/{MAX_CHOICES})
          </label>
          <button
            type="button"
            onClick={addChoice}
            disabled={choices.length >= MAX_CHOICES}
            className={[
              "flex items-center gap-1 text-xs font-medium",
              choices.length >= MAX_CHOICES
                ? "text-slate-300 cursor-not-allowed"
                : "text-violet-700 hover:text-violet-800",
            ].join(" ")}
          >
            <AddIcon style={{ fontSize: 16 }} />
            Add choice
          </button>
        </div>
        <div className="flex flex-col gap-2">
          {choices.map((choice, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-2"
            >
              <Select
                value={choice.emoji || ""}
                onChange={(e) =>
                  updateChoice(idx, { emoji: e.target.value || null })
                }
                size="small"
                displayEmpty
                aria-label="Choice emoji"
                sx={{
                  minWidth: 60,
                  fontSize: 16,
                  ".MuiOutlinedInput-notchedOutline": { border: "none" },
                  borderRight: "1px solid #e2e8f0",
                  borderRadius: 0,
                  "& .MuiSelect-select": { py: 0.5, pl: 1, pr: "24px !important" },
                }}
              >
                <MenuItem value="">—</MenuItem>
                {QUICK_EMOJIS.map((e) => (
                  <MenuItem key={e} value={e}>
                    {e}
                  </MenuItem>
                ))}
              </Select>
              <input
                type="text"
                value={choice.text || ""}
                onChange={(e) =>
                  updateChoice(idx, { text: e.target.value })
                }
                maxLength={CHOICE_MAX}
                placeholder={`Option ${idx + 1}`}
                className="flex-1 px-2 py-1 text-sm focus:outline-none"
              />
              <span
                className={[
                  "text-[11px] tabular-nums",
                  (choice.text || "").length > CHOICE_MAX
                    ? "text-red-600 font-semibold"
                    : "text-slate-400",
                ].join(" ")}
              >
                {(choice.text || "").length}/{CHOICE_MAX}
              </span>
              <IconButton
                size="small"
                onClick={() => removeChoice(idx)}
                disabled={choices.length <= MIN_CHOICES || optionsLocked}
                aria-label="Remove choice"
                title={
                  optionsLocked
                    ? "This poll has responses — options can't be removed"
                    : "Remove choice"
                }
              >
                <CloseIcon style={{ fontSize: 16 }} />
              </IconButton>
            </div>
          ))}
        </div>
        {optionsLocked && (
          <div className="mt-2 text-xs text-slate-500">
            This poll has responses, so options can't be removed (removing one
            would re-tally existing votes). You can still rename an option or add
            a new one.
          </div>
        )}
      </div>

      {/* Show-percentages toggle */}
      <label className="flex items-start gap-2 cursor-pointer">
        <Checkbox
          checked={showPercentages}
          onChange={(e) =>
            updateContent({ show_percentages: e.target.checked })
          }
          size="small"
          sx={{
            color: "#cbd5e1",
            "&.Mui-checked": { color: "#6d28d9" },
            p: 0,
            mt: "2px",
          }}
        />
        <div>
          <div className="text-sm font-medium text-slate-900">
            Show vote percentages after answering
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            "62% of your team chose this" — the social-proof reveal that
            makes polls fun. Leave on unless you have a reason not to.
          </div>
        </div>
      </label>

      {/* Read-only results (manager view). Only shown once there are votes. */}
      {hasResults && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">
            Results
          </div>
          {publicTotal > 0 && (
            <PollResultsGroup
              title={`Public link · ${publicTotal} ${
                publicTotal === 1 ? "vote" : "votes"
              }`}
              counts={publicCounts}
              total={publicTotal}
              choices={choices}
            />
          )}
          {assignedTotal > 0 && (
            <PollResultsGroup
              title={`Assigned training · ${assignedTotal} ${
                assignedTotal === 1 ? "vote" : "votes"
              }`}
              counts={assignedCounts}
              total={assignedTotal}
              choices={choices}
            />
          )}
        </div>
      )}
    </div>
  );
}
